import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import SessionTimeoutIcon from '@/assets/images/svg/session_timeout.svg';
import ResponsiveDialog from '@/components/ResponsiveDialog';

interface SessionTimeoutModalProps {
  open: boolean;
  onStayLoggedIn: () => void;
  onLogout: () => void;
  onLogin?: () => void;
  remainingSeconds: number;
  isLoading?: boolean;
}

const SessionTimeoutModal = ({
  open,
  onStayLoggedIn,
  onLogout,
  onLogin,
  remainingSeconds,
  isLoading,
}: SessionTimeoutModalProps) => {
  const isTimedOut = remainingSeconds <= 0;
  return (
    <ResponsiveDialog
      // Hook for tour.css: during a Help & Tour the dialog is lifted above the
      // tour popover and kept clickable, so Stay Logged In still works.
      className='session-timeout-dialog'
      fullScreenOnMobile={false}
      maxWidth='xs'
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
        '& .MuiDialog-paper': {
          borderRadius: '8px',
        },
      }}
      open={open}
      disableEscapeKeyDown
      aria-labelledby='session-timeout-dialog-title'
    >
      <DialogTitle sx={{ pt: 1 }} id='session-timeout-dialog-title'>
        <Box
          display='flex'
          p={0}
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography variant={isTimedOut ? 'h6' : 'body1'}>
            {isTimedOut ? 'Session Timeout!' : 'You will be logged out soon'}
          </Typography>
          {/* {!isTimedOut && ( */}
          <IconButton aria-label='close' onClick={onStayLoggedIn}>
            <CloseIcon sx={{ opacity: '0.6' }} />
          </IconButton>
          {/* )} */}
        </Box>
        <Box
          sx={{
            borderTop: '1px dashed rgba(223, 223, 223, 1)',
            borderBottom: '1px dashed rgba(223, 223, 223, 1)',
            // Full-bleed dashed divider. Phones bleed exactly across the
            // title's 24px side padding so it never widens the dialog.
            width: { xs: 'calc(100% + 48px)', sm: 'calc(100% + 216px)' },
            marginLeft: { xs: '-24px', sm: -24 },
            mt: 1,
          }}
        />
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isTimedOut ? (
            <>
              <Box
                component='img'
                src={SessionTimeoutIcon}
                alt='Session Timeout'
                sx={{
                  mt: 1,
                  mb: 2,
                  width: 40,
                  height: 40,
                }}
              />
              <Typography
                textAlign='center'
                alignContent='center'
                variant='body1'
              >
                You have been logged out due to inactivity
              </Typography>
            </>
          ) : (
            <>
              <Typography
                textAlign='center'
                alignContent='center'
                variant='body1'
              >
                For your security, we log you out automatically when you have
                not been active for a certain period of time.
              </Typography>
              <Typography
                textAlign='center'
                alignContent='center'
                sx={{
                  color: '#EA626E !important',
                }}
                mt={2}
                variant='h3'
              >
                <strong>
                  {`${Math.floor(remainingSeconds / 60)
                    .toString()
                    .padStart(2, '0')}:${(remainingSeconds % 60)
                    .toString()
                    .padStart(2, '0')}`}
                </strong>{' '}
              </Typography>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          gap: 1,
          padding: 3,
          pt: 0,
          width: '100%',
          // Phones stack the two full-width buttons so labels stay on one line.
          flexDirection: { xs: 'column', sm: 'row' },
          '& > :not(style) ~ :not(style)': { ml: { xs: 0, sm: 1 } },
        }}
      >
        {isTimedOut ? (
          <Button
            onClick={onLogin}
            variant='contained'
            fullWidth
            color='primary'
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size='1rem' color='inherit' />
              ) : null
            }
          >
            Login
          </Button>
        ) : (
          <>
            <Button variant='outlined' onClick={onLogout} fullWidth>
              Logout Now
            </Button>
            <Button onClick={onStayLoggedIn} variant='contained' fullWidth>
              Stay Logged In
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default SessionTimeoutModal;
